import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Search, Sparkles, FileText, Trash2, Plus, Send, Loader2, X } from "lucide-react";
import { BArecordMark } from "@/components/BArecordMark";
import { PortalShell } from "@/components/PortalShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSafeMeAuth } from "@/context/SafeMeAuthContext";
import { useLaunchStatus } from "@/hooks/useLaunchStatus";
import { EarlyAccess } from "@/components/EarlyAccess";
import api, { formatApiError } from "@/lib/api";

const TYPES = ["email", "meeting", "call", "chat", "note"];
const RISK_STYLES = {
  high: "text-red-400 border-red-400/40",
  medium: "text-yellow-400 border-yellow-400/40",
  low: "text-green-400 border-green-400/40",
  unknown: "text-fog border-white/20",
};

export default function SafeMe() {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const safemeAuth = useSafeMeAuth();
  const launch = useLaunchStatus();
  const locked = launch?.locked;

  const load = async () => {
    if (launch && launch.locked) return;
    try {
      const [r, s] = await Promise.all([
        api.get("/safeme/records", { params: { q: q || undefined, type: typeFilter || undefined } }),
        api.get("/safeme/stats"),
      ]);
      setRecords(r.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [typeFilter, launch?.locked]);

  // Live auto-refresh: keep records & stats current without manual reload
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load();
    };
    const interval = setInterval(tick, 12000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
    // eslint-disable-next-line
  }, [typeFilter, q]);

  return (
    <PortalShell
      authOverride={safemeAuth}
      homePath="/barecord/login"
      roleLabel="BArecord"
      headerTagline="BArecord. Business Captured. Scored. Governed."
      brand={
        <span className="flex items-center gap-2.5">
          <BArecordMark size={18} className="text-paper" />
          <span className="font-display text-lg tracking-tight text-paper">BArecord</span>
        </span>
      }
      eyebrow="BArecord · Secure Communications Intelligence"
      title="Capture, secure & understand every conversation"
      subtitle="BArecord preserves your business communications with structured metadata and an embedded AI assistant that scores compliance risk, answers questions, and briefs your executives."
    >
      {locked ? (
        <EarlyAccess product="BArecord" launchDate={launch?.launch_date} />
      ) : (
      <>
      <div className="flex items-center gap-2 mb-5" data-testid="safeme-live-indicator">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
        </span>
        <span className="text-xs uppercase tracking-widest text-fog">Live · auto-updating</span>
      </div>

      {/* stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden mb-10" data-testid="safeme-stats">
          <Stat label="Records" value={stats.total} />
          <Stat label="High risk" value={stats.by_risk.high} accent="text-red-400" />
          <Stat label="Medium risk" value={stats.by_risk.medium} accent="text-yellow-400" />
          <Stat label="Low risk" value={stats.by_risk.low} accent="text-green-400" />
        </div>
      )}

      <Tabs defaultValue="records" className="w-full">
        <TabsList className="bg-surface border border-white/10 mb-8">
          <TabsTrigger value="records" data-testid="tab-records">Records</TabsTrigger>
          <TabsTrigger value="ask" data-testid="tab-ask">Ask AI</TabsTrigger>
          <TabsTrigger value="briefing" data-testid="tab-briefing">Executive Briefing</TabsTrigger>
        </TabsList>

        {/* RECORDS */}
        <TabsContent value="records">
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <form
              onSubmit={(e) => { e.preventDefault(); load(); }}
              className="flex-1 flex items-center gap-2 bg-surface border border-white/10 rounded-full px-4"
            >
              <Search size={16} className="text-fog" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search communications…"
                data-testid="safeme-search"
                className="flex-1 bg-transparent py-2.5 text-sm text-paper outline-none placeholder:text-fog/60"
              />
            </form>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              data-testid="safeme-type-filter"
              className="bg-surface border border-white/10 rounded-full px-4 py-2.5 text-sm text-paper outline-none"
            >
              <option value="">All types</option>
              {TYPES.map((t) => <option key={t} value={t} className="bg-black capitalize">{t}</option>)}
            </select>
            <button
              onClick={() => setShowForm(true)}
              data-testid="safeme-capture-btn"
              className="inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-5 py-2.5 hover:bg-white transition-colors"
            >
              <Plus size={16} /> Capture
            </button>
          </div>

          {records.length === 0 ? (
            <Empty text="No communications captured yet. Click Capture to add your first record." />
          ) : (
            <div className="space-y-3" data-testid="safeme-records-list">
              {records.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setDetail(r)}
                  data-testid={`safeme-record-${r.id}`}
                  className="w-full text-left rounded-2xl border border-white/10 bg-surface p-5 hover:bg-[#232326] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] uppercase tracking-widest text-fog border border-white/15 rounded-full px-2 py-0.5 capitalize">{r.type}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${RISK_STYLES[r.risk?.level] || RISK_STYLES.unknown}`}>
                          {r.risk?.level} risk
                        </span>
                      </div>
                      <p className="mt-2 font-display text-lg tracking-tight text-paper truncate">{r.title}</p>
                      <p className="text-sm text-fog mt-1 truncate">{r.participants || "—"} · {r.occurred_at}</p>
                    </div>
                    <div className="font-display text-2xl tabular-nums text-fog shrink-0">{r.risk?.score ?? "—"}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ASK */}
        <TabsContent value="ask"><AskPanel /></TabsContent>

        {/* BRIEFING */}
        <TabsContent value="briefing"><BriefingPanel /></TabsContent>
      </Tabs>

      {showForm && <CaptureForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {detail && <RecordDetail record={detail} onClose={() => setDetail(null)} onDeleted={() => { setDetail(null); load(); }} />}
      </>
      )}
    </PortalShell>
  );
}

function Stat({ label, value, accent = "text-paper" }) {
  return (
    <div className="bg-surface p-6">
      <div className={`font-display text-3xl lg:text-4xl tracking-tighter ${accent}`}>{value}</div>
      <div className="mt-2 text-xs uppercase tracking-widest text-fog">{label}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-sm text-fog">{text}</div>;
}

function useEscape(onClose) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
}

function CaptureForm({ onClose, onSaved }) {
  useEscape(onClose);
  const [form, setForm] = useState({ type: "email", title: "", participants: "", content: "", occurred_at: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/safeme/records", form);
      toast.success("Captured & scored by AI.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="safeme-capture-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d0d0f] p-7 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl tracking-tight text-paper">Capture communication</h2>
          <button type="button" onClick={onClose} data-testid="capture-close" className="text-fog hover:text-paper transition-colors"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-fog">Type</label>
            <select value={form.type} onChange={set("type")} data-testid="capture-type" className="mt-2 w-full bg-black border border-white/15 rounded-xl px-3 py-2.5 text-paper text-sm outline-none capitalize">
              {TYPES.map((t) => <option key={t} value={t} className="bg-black capitalize">{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-fog">Date</label>
            <input type="date" value={form.occurred_at} onChange={set("occurred_at")} data-testid="capture-date" className="mt-2 w-full bg-black border border-white/15 rounded-xl px-3 py-2.5 text-paper text-sm outline-none [color-scheme:dark]" />
          </div>
        </div>
        <Field label="Title" value={form.title} onChange={set("title")} testid="capture-title" required />
        <Field label="Participants" value={form.participants} onChange={set("participants")} testid="capture-participants" placeholder="e.g. alice@acme.com, bob@acme.com" />
        <div className="mb-5">
          <label className="text-xs uppercase tracking-widest text-fog">Content</label>
          <textarea value={form.content} onChange={set("content")} required rows={6} data-testid="capture-content" className="mt-2 w-full bg-black border border-white/15 rounded-xl px-3 py-2.5 text-paper text-sm outline-none resize-none" />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-full border border-white/20 text-paper text-sm py-3 hover:bg-white/5 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} data-testid="capture-submit" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium py-3 hover:bg-white transition-colors disabled:opacity-60">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Scoring…</> : "Capture & score"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div className="mb-4">
      <label className="text-xs uppercase tracking-widest text-fog">{label}</label>
      <input {...props} data-testid={testid} className="mt-2 w-full bg-black border border-white/15 rounded-xl px-3 py-2.5 text-paper text-sm outline-none placeholder:text-fog/50" />
    </div>
  );
}

function RecordDetail({ record, onClose, onDeleted }) {
  useEscape(onClose);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(record.content || "");

  useEffect(() => {
    if (record.content) return;
    api.get(`/safeme/records/${record.id}`)
      .then(({ data }) => setContent(data.content || ""))
      .catch(() => {});
  }, [record.id, record.content]);

  const summarize = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/safeme/records/${record.id}/summary`);
      setSummary(data.summary);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/safeme/records/${record.id}`);
      toast.success("Record deleted.");
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="safeme-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0d0d0f] p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-fog border border-white/15 rounded-full px-2 py-0.5 capitalize">{record.type}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${RISK_STYLES[record.risk?.level] || RISK_STYLES.unknown}`}>{record.risk?.level} risk · {record.risk?.score}</span>
            </div>
            <h2 className="mt-3 font-display text-2xl tracking-tight text-paper">{record.title}</h2>
            <p className="text-sm text-fog mt-1">{record.participants || "—"} · {record.occurred_at}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={remove} data-testid="safeme-delete-btn" className="text-fog hover:text-red-400 transition-colors"><Trash2 size={18} /></button>
            <button onClick={onClose} data-testid="safeme-detail-close" className="text-fog hover:text-paper transition-colors"><X size={18} /></button>
          </div>
        </div>
        {record.risk?.summary && (
          <div className="mt-5 rounded-xl border border-white/10 bg-surface p-4">
            <p className="text-xs uppercase tracking-widest text-fog mb-2 flex items-center gap-2"><Shield size={13} /> AI risk assessment</p>
            <p className="text-sm text-paper/90 leading-relaxed">{record.risk.summary}</p>
            {record.risk?.reasons?.length > 0 && (
              <ul className="mt-3 space-y-1 list-disc pl-5">
                {record.risk.reasons.slice(0, 6).map((r, i) => <li key={i} className="text-xs text-fog leading-relaxed">{r}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="mt-5">
          <p className="text-xs uppercase tracking-widest text-fog mb-2 flex items-center gap-2"><FileText size={13} /> Content</p>
          <p className="text-sm text-fog leading-relaxed whitespace-pre-wrap">{content}</p>
        </div>

        <button onClick={summarize} disabled={loading} data-testid="safeme-summarize-btn" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 text-paper text-sm font-medium px-5 py-2.5 hover:bg-white/5 transition-colors disabled:opacity-60">
          {loading ? <><Loader2 size={15} className="animate-spin" /> Summarizing…</> : <><Sparkles size={15} /> AI summary</>}
        </button>
        {summary && <p className="mt-4 text-sm text-paper/90 leading-relaxed whitespace-pre-wrap rounded-xl border border-white/10 bg-surface p-4">{summary}</p>}
      </div>
    </div>
  );
}

function AskPanel() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setAnswer("");
    try {
      const { data } = await api.post("/safeme/ask", { question });
      setAnswer(data.answer);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={ask} className="flex items-center gap-2 bg-surface border border-white/10 rounded-full px-4">
        <Sparkles size={16} className="text-fog" />
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask across all your communications…" data-testid="safeme-ask-input" className="flex-1 bg-transparent py-3 text-sm text-paper outline-none placeholder:text-fog/60" />
        <button type="submit" disabled={loading} data-testid="safeme-ask-submit" className="inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-5 py-2 my-1 hover:bg-white transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </form>
      {answer && <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-6 text-sm text-paper/90 leading-relaxed whitespace-pre-wrap" data-testid="safeme-answer">{answer}</div>}
      {!answer && !loading && <Empty text="Ask a question like: 'Which communications mention payments?' or 'Summarize risks from last week.'" />}
    </div>
  );
}

function BriefingPanel() {
  const [briefing, setBriefing] = useState("");
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("week");

  const generate = async () => {
    setLoading(true);
    try {
      const params = period === "week" ? { days: 7 } : {};
      const { data } = await api.get("/safeme/briefing", { params });
      setBriefing(data.briefing);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-white/15 p-1" data-testid="briefing-period-toggle">
          {[
            { k: "week", label: "This week" },
            { k: "all", label: "All time" },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setPeriod(o.k)}
              data-testid={`briefing-period-${o.k}`}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                period === o.k ? "bg-paper text-black" : "text-fog hover:text-paper"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <button onClick={generate} disabled={loading} data-testid="safeme-briefing-btn" className="inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-6 py-3 hover:bg-white transition-colors disabled:opacity-60">
          {loading ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Sparkles size={15} /> Generate {period === "week" ? "weekly" : "executive"} briefing</>}
        </button>
      </div>
      {briefing && <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-6 text-sm text-paper/90 leading-relaxed whitespace-pre-wrap" data-testid="safeme-briefing">{briefing}</div>}
    </div>
  );
}
